import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API, BACKEND_URL } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Loader2, ImageIcon, Film, RefreshCw, Share2, Copy, Eye } from "lucide-react";

const KINDS = {
  summary_image: { label: "Visual summary", icon: ImageIcon, cta: "Create visual summary", eta: "usually under a minute",
    desc: "One AI-made picture that sums up the document's key ideas." },
  video: { label: "Explainer video", icon: Film, cta: "Create explainer video", eta: "usually 2–5 minutes",
    desc: "A 5-second AI video that introduces the document, ready to share." },
};

function parseSections(text) {
  const parts = text.split(/\[Section (\d+)\]\n?/);
  const out = parts[0].trim() ? [{ n: 0, body: parts[0].trim() }] : [];
  for (let i = 1; i < parts.length; i += 2) out.push({ n: Number(parts[i]), body: (parts[i + 1] || "").trim() });
  return out;
}

function DocumentView({ doc, jump }) {
  const [text, setText] = useState(null);
  const [flash, setFlash] = useState(null);
  const isPdf = !doc.file_type || doc.file_type === "pdf";
  useEffect(() => {
    if (!isPdf) api.get(`/documents/${doc.id}/text`).then((r) => setText(r.data.text)).catch((e) => { console.warn("Document text failed to load", e); setText(""); });
  }, [doc.id, isPdf]);
  useEffect(() => {
    if (isPdf || !jump || text == null) return;
    const el = document.getElementById(`sec-${doc.id}-${jump.n}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setFlash(jump.n);
    const t = setTimeout(() => setFlash(null), 2200);
    return () => clearTimeout(t);
  }, [jump, text, doc.id, isPdf]);

  if (isPdf) {
    const page = jump?.n ? `#page=${jump.n}` : "";
    return <iframe key={jump?.t || "pdf"} title="pdf" data-testid="doc-pdf-view" src={`${API}/documents/${doc.id}/file?auth=${getToken()}${page}`} className="flex-1 w-full bg-background" />;
  }
  return (
    <div data-testid="doc-text-view" className="flex-1 overflow-y-auto bg-background p-8 text-[15px] leading-relaxed whitespace-pre-wrap">
      {text == null ? <Loader2 className="w-5 h-5 animate-spin" /> : parseSections(text).map((s) => (
        <div key={s.n} id={`sec-${doc.id}-${s.n}`} data-testid={`doc-section-${s.n}`}
          className={`scroll-mt-4 -mx-3 px-3 py-1 rounded-sm transition-colors duration-700 ${flash === s.n ? "bg-accent" : "bg-transparent"}`}>
          {s.n > 0 && <div className="font-mono-label text-primary mt-5 mb-2">Section {s.n}</div>}
          {s.body}
        </div>
      ))}
    </div>
  );
}

function ShareVideo({ job, onChange }) {
  const [busy, setBusy] = useState(false);
  // Server-rendered share page so WhatsApp, X and LinkedIn can show a title and still frame.
  const shareUrl = (token) => `${BACKEND_URL}/api/share/v/${token}`;
  const link = job.share_token ? shareUrl(job.share_token) : null;
  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.success("Link ready, copy it below"); }
  };
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await onChange(); } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); } finally { setBusy(false); }
  };
  if (!link) return (
    <Button data-testid="video-share-btn" disabled={busy} onClick={() => act(async () => {
      const { data } = await api.post(`/media/${job.result_id}/share`);
      await copy(shareUrl(data.share_token));
    })} className="rounded-sm bg-primary text-primary-foreground hover:bg-foreground btn-invert">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4 mr-2" /> Get share link</>}
    </Button>
  );
  return (
    <div className="w-full max-w-2xl text-left space-y-2" data-testid="video-share-panel">
      <div className="font-mono-label text-muted-foreground flex items-center justify-between gap-3">
        <span>Anyone with this link can watch</span>
        <span data-testid="video-share-views" className="flex items-center gap-1.5 text-foreground">
          <Eye className="w-3.5 h-3.5" /> {(job.view_count || 0).toLocaleString()} {job.view_count === 1 ? "view" : "views"}
        </span>
      </div>
      <div className="flex gap-2">
        <Input readOnly value={link} data-testid="video-share-link" className="rounded-sm bg-background" onFocus={(e) => e.target.select()} />
        <Button data-testid="video-share-copy" variant="outline" className="rounded-sm" onClick={() => copy(link)}><Copy className="w-4 h-4" /></Button>
        <Button data-testid="video-share-stop" variant="ghost" disabled={busy} className="rounded-sm text-destructive"
          onClick={() => act(async () => { await api.delete(`/media/${job.result_id}/share`); toast.success("Link turned off"); })}>
          Stop sharing
        </Button>
      </div>
    </div>
  );
}

function MediaPanel({ doc, kind, job, onCreate, onChange }) {
  const k = KINDS[kind];
  const busy = job && ["queued", "running"].includes(job.status);
  const src = job?.result_id ? `${API}/media/${job.result_id}/file?auth=${getToken()}` : null;
  return (
    <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center text-center gap-5">
      {src ? (
        kind === "video"
          ? <video data-testid="media-video" src={src} controls className="w-full max-w-2xl border border-border bg-black"
              poster={job?.share_token ? `${API}/public/videos/${job.share_token}/poster.jpg` : undefined} />
          : <img data-testid="media-summary-image" src={src} alt={`Visual summary of ${doc.filename}`} className="w-full max-w-2xl border border-border object-contain bg-background" />
      ) : (
        <>
          <k.icon strokeWidth={1.2} className="w-10 h-10 text-primary" />
          <div>
            <h3 className="font-serif-display text-2xl mb-2">{k.label}</h3>
            <p className="text-sm text-muted-foreground max-w-sm">{k.desc}</p>
          </div>
        </>
      )}
      {busy && <div data-testid={`media-status-${kind}`} className="font-mono-label text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Creating… {k.eta}</div>}
      {kind === "video" && src && !busy && <ShareVideo job={job} onChange={onChange} />}
      {job?.status === "failed" && (
        <div data-testid={`media-error-${kind}`} className={`text-sm max-w-sm ${src ? "text-muted-foreground" : "text-destructive"}`}>
          {src ? "Your last attempt didn't finish, so this is the previous version." : job.error}
        </div>
      )}
      {!busy && (
        <Button data-testid={`media-create-${kind}`} onClick={() => onCreate(kind)} variant={src ? "outline" : "default"}
          className={`rounded-sm ${src ? "" : "bg-foreground text-background hover:bg-primary btn-invert"}`}>
          {src ? <><RefreshCw className="w-4 h-4 mr-2" /> Make a new one</> : k.cta}
        </Button>
      )}
    </div>
  );
}

export default function DocVisuals({ doc, jump }) {
  const [jobs, setJobs] = useState({});
  const [tab, setTab] = useState("document");
  const timer = useRef(null);
  useEffect(() => { if (jump) setTab("document"); }, [jump]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/documents/${doc.id}/media`);
      setJobs(data);
      const pending = Object.values(data).some((j) => ["queued", "running"].includes(j.status));
      clearTimeout(timer.current);
      if (pending) timer.current = setTimeout(load, 4000);
    } catch (e) {
      console.warn("Media status check failed, retrying", e);
      clearTimeout(timer.current);
      timer.current = setTimeout(load, 8000);
    }
  }, [doc.id]);
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);

  const create = async (kind) => {
    try {
      await api.post(`/documents/${doc.id}/media`, { kind });
      toast.success(`${KINDS[kind].label} started — ${KINDS[kind].eta}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start");
    }
  };

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
      <TabsList className="rounded-none justify-start bg-background border-b border-border h-11 px-2">
        <TabsTrigger value="document" data-testid="left-tab-document" className="font-mono-label rounded-sm">Document</TabsTrigger>
        <TabsTrigger value="summary_image" data-testid="left-tab-summary-image" className="font-mono-label rounded-sm">Visual summary</TabsTrigger>
        <TabsTrigger value="video" data-testid="left-tab-video" className="font-mono-label rounded-sm">Video</TabsTrigger>
      </TabsList>
      <TabsContent value="document" className="flex-1 flex flex-col m-0 data-[state=inactive]:hidden"><DocumentView doc={doc} jump={jump} /></TabsContent>
      {Object.keys(KINDS).map((kind) => (
        <TabsContent key={kind} value={kind} className="flex-1 flex flex-col m-0 data-[state=inactive]:hidden">
          <MediaPanel doc={doc} kind={kind} job={jobs[kind]} onCreate={create} onChange={load} />
        </TabsContent>
      ))}
    </Tabs>
  );
}
